import * as THREE from 'three';
import { Orb } from '../../orbs/Orb';
import { global } from '../../core/GlobalState';
import { createRoundedCylinderBackside } from './ObjectCreation';
const sprayDefaults = {
    baseVelocity: 5,
    sprayCoefficient: 2100,
    timeGap: () => global.baseTimeGap, // ensure we read the current value
};
const stackRegionOrbsByScene = new WeakMap();
function getOrCreateStackRegionMap(scene) {
    const existing = stackRegionOrbsByScene.get(scene);
    if (existing) {
        return existing;
    }
    const created = new Map();
    stackRegionOrbsByScene.set(scene, created);
    return created;
}
export function getStackRegionOrb(scene, label) {
    var _a;
    return (_a = stackRegionOrbsByScene.get(scene)) === null || _a === void 0 ? void 0 : _a.get(label);
}
const fetcherByRegion = {
    'Domain Region': { fetcher: 'domainOrbs', label: 'DOMAIN_ORB' },
    'Network Gateway Region': { fetcher: 'networkGatewayOrbs', label: 'NETWORK_GATEWAY_ORB' },
    'Container Endpoint Region': { fetcher: 'endpointOrbs', label: 'CONTAINER_ENDPOINT_ORB' },
    'Docker Container Region': { fetcher: 'dockerOrbs', label: 'DOCKER_CONTAINER_ORB' },
    'Container Image Region': { fetcher: 'registryOrbs', label: 'CONTAINER_IMAGE_ORB' },
    'GitHub Repository Region': { fetcher: 'githubOrbs', label: 'GITHUB_ORB' },
    'Code Region': { fetcher: 'textObjectOrbs', label: 'TEXT_OBJECT_ORB' },
    'Database Object Region': { fetcher: 'databaseObjectOrbs', label: 'DATABASE_OBJECT_ORB' },
    'Computer Region': { fetcher: 'dockerSwarmOrbs', label: 'COMPUTER_ORB' },
};
export function buildGiantRainbowCylinderStack(scene, position, sizing = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const regionMap = getOrCreateStackRegionMap(scene);
    const baseX = position.x;
    const baseZ = position.z;
    const startY = position.y;
    const layerHeight = (_a = sizing.layerHeight) !== null && _a !== void 0 ? _a : 900;
    const layerGap = (_b = sizing.layerGap) !== null && _b !== void 0 ? _b : 800;
    const baseRadius = (_c = sizing.baseRadius) !== null && _c !== void 0 ? _c : 3000;
    const bevelRadius = 300;
    const typeColorPairs = [
        { label: 'Domain Region', colors: [0xf48120, 0xf0f0f0], radii: baseRadius + 2500 },
        { label: 'Network Gateway Region', colors: [0x1a0081, 0x00d084], radii: baseRadius - 200 },
        { label: 'Container Endpoint Region', colors: [0x84EC2C, 0x173647], radii: baseRadius + 300 },
        { label: 'Docker Container Region', colors: [0x3496d3, 0x1D63ED], radii: baseRadius + 1800 },
        { label: 'Computer Region', colors: [0x2d3ec3, 0x9ca3db], radii: baseRadius - 600 },
        { label: 'Container Image Region', colors: [0x262262, 0x5f5fbb], radii: baseRadius - 200 },
        { label: 'GitHub Repository Region', colors: [0xf04c34, 0xf29086], radii: baseRadius + 1000 },
        { label: 'Code Region', colors: [0x338899, 0x88f0ff], radii: baseRadius + 1300 },
        { label: 'Database Object Region', colors: [0xff2cff, 0x2428ff], radii: baseRadius - 500 },
    ];
    // bottom → top
    const stack = [...typeColorPairs].reverse();
    regionMap.clear();
    for (let i = 0; i < stack.length; i++) {
        const { label, colors, radii } = stack[i];
        const [innerColor, outerColor] = colors;
        const y = startY + i * (layerHeight + layerGap);
        const pos = new THREE.Vector3(baseX, y, baseZ);
        const resolvedRadius = (_f = (_e = (_d = sizing.regionRadiusOverrides) === null || _d === void 0 ? void 0 : _d[label]) !== null && _e !== void 0 ? _e : radii) !== null && _f !== void 0 ? _f : baseRadius;
        const resolvedHeight = (_h = (_g = sizing.regionHeightOverrides) === null || _g === void 0 ? void 0 : _g[label]) !== null && _h !== void 0 ? _h : layerHeight;
        // === Orb config for the cylindrical layer ===
        const orbConfig = {
            orbType: `STACK_${label}`,
            radius: resolvedRadius,
            defaultLayerSide: THREE.BackSide,
            defaultLayerScaleFactor: 0.06,
            // you can add textColor, etc., if you want per-layer label styling
            layers: [
                {
                    shape: 'roundedCylinder',
                    color: innerColor,
                    scaleFactor: 0.0,
                    geometryParams: {
                        shape: 'roundedCylinder',
                        height: resolvedHeight,
                        bevelRadius,
                        radialSegments: 96,
                        bevelSegments: 16,
                    },
                },
                {
                    shape: 'roundedCylinder',
                    color: outerColor,
                    geometryParams: {
                        shape: 'roundedCylinder',
                        height: resolvedHeight,
                        bevelRadius,
                        radialSegments: 96,
                        bevelSegments: 16,
                    },
                },
            ],
        };
        const orb = new Orb(orbConfig, {}, label);
        orb.setPosition(pos);
        // === Imbue with activation logic (spray from itself) ===
        const spec = fetcherByRegion[label];
        if (spec) {
            orb.addAction({
                trigger: 'onActivate',
                kind: 'sprayFromFetcher',
                params: {
                    fetcher: spec.fetcher,
                    args: {},
                    baseVelocity: sprayDefaults.baseVelocity,
                    sprayCoefficient: sprayDefaults.sprayCoefficient,
                    timeGap: sprayDefaults.timeGap(),
                    useCache: true,
                },
            });
        }
        else {
            // If an unmapped label sneaks in, keep a safe default behavior (no-op).
            // You could also log a warning instead:
            // console.warn(`[buildStack] No fetcher mapping for label: ${label}`);
        }
        // Optional: also support "callOrbAction" or batch connects later if you want
        // orb.addAction({ trigger: 'onActivate', kind: 'connectByRelationships', params: {...} });
        orb.addToScene(scene);
        if (orb.group) {
            orb.group.name = `stackRegion:${label}`;
        }
        regionMap.set(label, orb);
        // If your system defers mesh attachments, you can normalize after frame:
        // requestAnimationFrame(() => OrbFactory.normalizeOrbSize(orb, radii ?? baseRadius));
    }
}
export function calculateDynamicStackSizing(layerSpecs) {
    const baseLayerHeight = 900;
    const baseLayerGap = 800;
    const minRadius = 1900;
    const baseRadius = 2400;
    const maxRadius = 6400;
    const layerRadii = new Map();
    for (const layer of layerSpecs) {
        const count = Math.max(0, layer.itemCount);
        const countBasedRadius = baseRadius + Math.max(0, count - 6) * 115;
        const resolved = Math.min(maxRadius, Math.max(minRadius, countBasedRadius));
        layerRadii.set(layer.label, resolved);
    }
    const maxCount = layerSpecs.reduce((acc, layer) => Math.max(acc, layer.itemCount), 0);
    const layerGap = baseLayerGap + Math.max(0, maxCount - 8) * 24;
    return {
        layerHeight: baseLayerHeight,
        layerGap,
        layerRadii,
    };
}
// Old versions
export function buildGiantRainbowCylinderStackOlder(scene) {
    // ----- Master config -----
    const baseX = 10000;
    const baseZ = 0;
    const startY = 1500;
    const layers = 20;
    const layerHeight = 2000;
    const layerGap = 1000; // space between layers (stacking along +Y)
    const baseRadius = 6000;
    const bevelRadius = 300; // rounded rim size
    const stackGroup = new THREE.Group();
    stackGroup.name = 'GiantRainbowCylinderStack';
    scene.add(stackGroup);
    const colorForLayer = (i, total) => {
        const hue = i / Math.max(1, total); // [0,1)
        return new THREE.Color().setHSL(hue, 0.85, 0.55).getHex();
    };
    for (let i = 0; i < layers; i++) {
        const color = colorForLayer(i, layers);
        const y = startY + i * (layerHeight + layerGap);
        const pos = new THREE.Vector3(baseX, y, baseZ);
        const cyl = createRoundedCylinderBackside(baseRadius, layerHeight, bevelRadius, color, pos, 96, // radialSegments (smoother than default)
        16 // bevelSegments
        );
        // If you want lighting/shadows, swap material to Phong below:
        // (cyl.material as THREE.Material).dispose();
        // cyl.material = new THREE.MeshPhongMaterial({
        //   color,
        //   shininess: 60,
        //   specular: 0x444444,
        //   side: THREE.BackSide
        // });
        cyl.castShadow = true;
        cyl.receiveShadow = true;
        stackGroup.add(cyl);
    }
    // Optional dramatic transform:
    // stackGroup.rotation.y = 0.05;
    // stackGroup.position.z = -600;
}
export function buildGiantRainbowCylinderStackOld(scene) {
    // ----- Placement/master config -----
    const baseX = 10000;
    const baseZ = 0;
    const startY = 1500;
    const layerHeight = 2000;
    const layerGap = 1000;
    const baseRadius = 6000;
    const bevelRadius = 300;
    const stackGroup = new THREE.Group();
    stackGroup.name = 'GiantRainbowCylinderStack';
    scene.add(stackGroup);
    // ----- Palette derived from orb layers (TOP → BOTTOM) --------------------
    const palette = [
        // DOMAIN_ORB
        0xf48120,
        // NETWORK_GATEWAY_ORB
        0x1a0081,
        // CONTAINER_ENDPOINT_ORB (genLangOrb(0x84EC2C, 0x173647))
        0x84EC2C,
        // DOCKER_CONTAINER_ORB
        0x3496d3,
        // CONTAINER_IMAGE_ORB
        0x5f5fbb,
        // GITHUB_ORB
        0xf04c34,
        // DATABASE_OBJECT_ORB
        0xff2cff,
        // COMPUTER_ORB
        0x2d3ec3,
    ];
    for (let i = 0; i < palette.length; i++) {
        const color = palette[palette.length - 1 - i]; //QUICK REVERSE FIX, rather than retype the palette
        const y = startY + i * (layerHeight + layerGap);
        const pos = new THREE.Vector3(baseX, y, baseZ);
        const cyl = createRoundedCylinderBackside(baseRadius, layerHeight, bevelRadius, color, pos, 96, // radial segments
        16 // bevel segments
        );
        cyl.castShadow = true;
        cyl.receiveShadow = true;
        stackGroup.add(cyl);
    }
    // Optional dramatic transform:
    // stackGroup.rotation.y = 0.05;
    // stackGroup.position.z = -600;
}
export function buildGiantRainbowCylinderStackPreOrb(scene) {
    // ----- Placement/master config -----
    const baseX = 10000;
    const baseZ = 0;
    const startY = 2600;
    const layerHeight = 1500; // visual "thickness" — your roundedCylinder builder should respect it (via OrbResources)
    const layerGap = 3000;
    const baseRadius = 3000;
    const bevelRadius = 300; // likewise passed via your custom geometry mapping (or defaulted)
    const stackGroup = new THREE.Group();
    stackGroup.name = 'GiantRainbowCylinderStack';
    scene.add(stackGroup); //TOOD don't do this, isntead acutally put each orb down separately
    // ----- Derive top→bottom types with their OUTER TWO layer colors -----
    // We focus on the *outer two* colors for each type; both layers use shape 'roundedCylinder'.
    // Listed TOP → BOTTOM, then we'll reverse() once so iteration is straightforward.
    const typeColorPairs = [
        // DOMAIN_ORB
        { label: 'DOMAIN_ORB', colors: [0xf48120, 0xf0f0f0] },
        // NETWORK_GATEWAY_ORB
        { label: 'NETWORK_GATEWAY_ORB', colors: [0x1a0081, 0x009900] },
        // CONTAINER_ENDPOINT_ORB (genLangOrb(0x84EC2C, 0x173647))
        { label: 'CONTAINER_ENDPOINT_ORB', colors: [0x84EC2C, 0x173647] },
        // DOCKER_CONTAINER_ORB (outer two are the sphere layers: 0x1D63ED, 0xf0f0f0)
        { label: 'DOCKER_CONTAINER_ORB', colors: [0x3496d3, 0x1D63ED] },
        // CONTAINER_IMAGE_ORB — not fully specified; keep your previous accent for now
        { label: 'CONTAINER_IMAGE_ORB', colors: [0x262262, 0x5f5fbb] },
        // GITHUB_ORB (outer two sphere layers: 0xf29086, 0xf0f0f0)
        { label: 'GITHUB_ORB', colors: [0xf04c34, 0xf29086] },
        // DATABASE_OBJECT_ORB (has exactly two)
        { label: 'DATABASE_OBJECT_ORB', colors: [0xff2cff, 0x2428ff] },
        // COMPUTER_ORB (outer two sphere layers: 0x9ca3db, 0x2d3ec3)
        { label: 'COMPUTER_ORB', colors: [0x2d3ec3, 0x9ca3db] },
    ];
    // Fix ordering cleanly: invert once so the first pushed orb is actually the BOTTOM, rising upward.
    const stack = [...typeColorPairs].reverse();
    for (let i = 0; i < stack.length; i++) {
        const { label, colors } = stack[i];
        const [innerColor, outerColor] = colors;
        const y = startY + i * (layerHeight + layerGap);
        const pos = new THREE.Vector3(baseX, y, baseZ);
        // Double-layer rounded-cylinder orb for this tier
        const orbConfig = {
            orbType: `STACK_${label}`,
            radius: baseRadius,
            defaultLayerSide: THREE.BackSide,
            defaultLayerScaleFactor: 0.08,
            textColor: 0xf0f0f0,
            textOutlineColor: 0xf0f0f0,
            // If your OrbResources mapping needs height/bevel, pass via shared prefs or
            // encode them in the geometry mapping for 'roundedCylinder'.
            layers: [
                { shape: 'roundedCylinder', color: innerColor, scaleFactor: 0.00 },
                { shape: 'roundedCylinder', color: outerColor }, // outer shell
            ],
        };
        const orb = new Orb(orbConfig, {}, label);
        orb.setPosition(pos);
        // If you have a way to feed height/bevelRadius into your geometry builder globally, do it here.
        // e.g., RenderingPreferences.setRoundedCylinderDims({ height: layerHeight, bevelRadius });
        // Add to the local stack group so we can transform the entire tower later if desired
        const group = orb.createOrb();
        stackGroup.add(group);
    }
    // Optional: dramatic transform for the whole tower
    // stackGroup.rotation.y = 0.05;
    // stackGroup.position.z = -600;
}
export function buildGiantRainbowCylinderStackGreat(scene) {
    const baseX = 12000;
    const baseZ = 0;
    const startY = 1300;
    const layerHeight = 500;
    const layerGap = 600;
    const baseRadius = 3000;
    const bevelRadius = 300;
    const typeColorPairs = [
        { label: 'Domain Region', colors: [0xf48120, 0xf0f0f0], radii: baseRadius + 1000 },
        { label: 'Network Gateway Region', colors: [0x1a0081, 0x00d084], radii: baseRadius - 200 },
        { label: 'Container Endpoint Region', colors: [0x84EC2C, 0x173647], radii: baseRadius + 300 },
        { label: 'Docker Container Region', colors: [0x3496d3, 0x1D63ED], radii: baseRadius + 1800 },
        { label: 'Container Image Region', colors: [0x262262, 0x5f5fbb], radii: baseRadius - 600 },
        { label: 'GitHub Repository Region', colors: [0xf04c34, 0xf29086], radii: baseRadius + 1000 },
        { label: 'Code Region', colors: [0x338899, 0x88f0ff], radii: baseRadius + 1500 },
        { label: 'Database Object Region', colors: [0xff2cff, 0x2428ff], radii: baseRadius - 500 },
        { label: 'Computer Region', colors: [0x2d3ec3, 0x9ca3db], radii: baseRadius - 1500 },
    ];
    const stack = [...typeColorPairs].reverse(); // bottom → top
    for (let i = 0; i < stack.length; i++) {
        const { label, colors, radii } = stack[i];
        const [innerColor, outerColor] = colors;
        const y = startY + i * (layerHeight + layerGap);
        const pos = new THREE.Vector3(baseX, y, baseZ);
        const orbConfig = {
            orbType: `STACK_${label}`,
            radius: radii,
            defaultLayerSide: THREE.BackSide,
            defaultLayerScaleFactor: 0.06,
            layers: [
                {
                    shape: 'roundedCylinder',
                    color: innerColor,
                    scaleFactor: 0.0,
                    //transparent: true,
                    //opacity: 0.7,
                    geometryParams: {
                        shape: 'roundedCylinder',
                        height: layerHeight,
                        bevelRadius,
                        radialSegments: 96,
                        bevelSegments: 16,
                    },
                },
                {
                    shape: 'roundedCylinder',
                    color: outerColor,
                    //transparent: true,
                    //opacity: 0.7,
                    geometryParams: {
                        shape: 'roundedCylinder',
                        height: layerHeight,
                        bevelRadius,
                        radialSegments: 96,
                        bevelSegments: 16,
                    },
                },
            ],
        };
        const orb = new Orb(orbConfig, {}, label);
        orb.setPosition(pos);
        orb.addToScene(scene);
    }
}
